// The stationery CATEGORIES listed here mirror the Admin Stationery dropdown,
// whose source-of-truth lives in `@apps/admin/routes/Dashboard/components/DashboardContentV2/hooks/useStationeryMenu`.
// If you add or reorder items in that hook, keep this list in sync. CategoryNav
// uses `getShopPath(...)` for in-product design browsing — URL semantics differ
// from the admin dropdown's `/cards/wedding/<category>` routes, so a runtime
// dependency on useStationeryMenu is not appropriate here.
import { useMemo, useState } from 'react';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useStationeryTemplateCategoryTranslations } from '@apps/card/hooks/useStationeryTemplateCategoryTranslations';
import { useCardCategoryContext } from '@apps/card/components/CardCategoryProvider';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useQueryParamHelper } from '@shared/core/queryString';

export type NavItemType =
  | {
      type: 'category';
      category: string | StationeryTemplateCategoryEnum;
      title: string;
      isActive: () => boolean;
      to: string;
    }
  | {
      type: 'subcategory';
      category: string | StationeryTemplateCategoryEnum;
      title: string;
      isActive: () => boolean;
      children: Array<{
        category: string;
        title: string;
        to: string;
        isActive: () => boolean;
        thumbnailUrl: string;
      }>;
    };

export type CategoryNavItem = Extract<NavItemType, { type: 'category' }>;
export type SubcategoryNavItem = Extract<NavItemType, { type: 'subcategory' }>;
export type SubcategoryChild = SubcategoryNavItem['children'][number];

const usePreserveQueryParams = () => {
  const { getValueString } = useQueryParamHelper();

  return (path: string, params: string[]) => {
    const searchParams = new URLSearchParams();

    params.forEach(param => {
      const value = getValueString(param);

      if (value) {
        searchParams.set(param, value);
      }
    });

    const cleanPath = path.split('?')[0];

    return `${cleanPath}${Array.from(searchParams.entries()).length ? '?' : ''}${searchParams.toString()}`;
  };
};

export const useNavItems = () => {
  const { getShopPath } = useCardsRouterContext();
  const getCategoryTitle = useStationeryTemplateCategoryTranslations('dashboardCategoryNav');

  const [expandedCategoryIndex, setExpandedCategoryIndex] = useState<number>(-1);
  const { currentCategory } = useCardCategoryContext();
  const preserveQueryParams = usePreserveQueryParams();

  const navItems = useMemo<NavItemType[]>(() => {
    // Order mirrors the Admin Stationery dropdown (see @apps/admin/routes/Dashboard/components/DashboardContentV2/hooks/useStationeryMenu).
    const items: NavItemType[] = [
      {
        type: 'category',
        category: StationeryTemplateCategoryEnum.saveTheDate,
        title: getCategoryTitle(StationeryTemplateCategoryEnum.saveTheDate),
        isActive: () => currentCategory === StationeryTemplateCategoryEnum.saveTheDate,
        to: preserveQueryParams(getShopPath('wedding', StationeryTemplateCategoryEnum.saveTheDate), ['format']),
      },
      {
        type: 'category',
        category: StationeryTemplateCategoryEnum.invitation,
        title: getCategoryTitle(StationeryTemplateCategoryEnum.invitation),
        isActive: () => currentCategory === StationeryTemplateCategoryEnum.invitation,
        to: preserveQueryParams(getShopPath('wedding', StationeryTemplateCategoryEnum.invitation), ['format']),
      },
      {
        type: 'category',
        category: StationeryTemplateCategoryEnum.thankYou,
        title: getCategoryTitle(StationeryTemplateCategoryEnum.thankYou),
        isActive: () => currentCategory === StationeryTemplateCategoryEnum.thankYou,
        to: preserveQueryParams(getShopPath('wedding', StationeryTemplateCategoryEnum.thankYou), ['format']),
      },
      {
        type: 'category',
        category: StationeryTemplateCategoryEnum.sign,
        title: getCategoryTitle(StationeryTemplateCategoryEnum.sign),
        isActive: () => currentCategory === StationeryTemplateCategoryEnum.sign,
        to: preserveQueryParams(getShopPath('wedding', StationeryTemplateCategoryEnum.sign), ['format']),
      },
      {
        type: 'category',
        category: StationeryTemplateCategoryEnum.menu,
        title: getCategoryTitle(StationeryTemplateCategoryEnum.menu),
        isActive: () => currentCategory === StationeryTemplateCategoryEnum.menu,
        to: preserveQueryParams(getShopPath('wedding', StationeryTemplateCategoryEnum.menu), ['format']),
      },
      {
        type: 'category',
        category: StationeryTemplateCategoryEnum.napkin,
        title: getCategoryTitle(StationeryTemplateCategoryEnum.napkin),
        isActive: () => currentCategory === StationeryTemplateCategoryEnum.napkin,
        to: preserveQueryParams(getShopPath('wedding', StationeryTemplateCategoryEnum.napkin), ['format']),
      },
      {
        type: 'category',
        category: StationeryTemplateCategoryEnum.enclosure,
        title: getCategoryTitle(StationeryTemplateCategoryEnum.enclosure),
        isActive: () => currentCategory === StationeryTemplateCategoryEnum.enclosure,
        to: preserveQueryParams(getShopPath('wedding', StationeryTemplateCategoryEnum.enclosure), ['format']),
      },
      {
        type: 'category',
        category: StationeryTemplateCategoryEnum.holiday,
        title: getCategoryTitle(StationeryTemplateCategoryEnum.holiday),
        isActive: () => currentCategory === StationeryTemplateCategoryEnum.holiday,
        to: preserveQueryParams(getShopPath('wedding', StationeryTemplateCategoryEnum.holiday), ['format']),
      },
    ];

    return items;
  }, [currentCategory, getCategoryTitle, getShopPath, preserveQueryParams]);

  const onExpandCategoryClick = useEventCallback((idx: number) => {
    setExpandedCategoryIndex(prev => (prev === idx ? -1 : idx));
  });

  return {
    navItems,
    expandedCategoryIndex,
    onExpandCategoryClick,
  };
};
